import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { 
  initializeExport, 
  startExport, 
  completeExport, 
  failExport 
} from '../store/slices/export.slice';
import toast from 'react-hot-toast';

/**
 * useExport Hook
 * A simple hook that makes exporting data super easy with minimal code
 * 
 * @returns {Object} - Export functions and utilities
 */
const useExport = () => {
  const dispatch = useDispatch();

  /**
   * Simple export function - just pass the API function and parameters
   * @param {Object} options - Export options
   * @param {Function} options.apiFunction - The API function to call (e.g., exportUser, exportStaff, etc.)
   * @param {Object} options.apiParams - Parameters to pass to the API function
   * @param {string} options.exportName - Name for the export task (default: "Data Export")
   * @param {string} options.exportType - Type of export (default: "excel")
   * @param {string} options.fileName - Custom filename (optional)
   * @param {Function} options.onSuccess - Callback when export succeeds (optional)
   * @param {Function} options.onError - Callback when export fails (optional)
   * @returns {Promise} - Export promise
   */
  const exportData = useCallback(async ({
    apiFunction,
    apiParams = {},
    exportName = "Data Export",
    exportType = "excel",
    fileName = null,
    onSuccess = null,
    onError = null
  }) => {
    try {
      // Generate export ID
      const exportId = `export_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Initialize export in Redux store
      dispatch(initializeExport({
        exportId,
        exportName,
        exportType
      }));

      // Mark export as started
      dispatch(startExport({ exportId }));

      // Make API call
      const response = await apiFunction(apiParams);

      const body = response?.data;
      const downloadUrl =
        body?.url ?? body?.data?.url ?? body?.file_url ?? body?.data?.file_url;

      // Handle successful response (support { url }, { data: { url } }, { file_url })
      if (downloadUrl) {
        const finalFileName =
          fileName ||
          body?.file_name ||
          body?.data?.file_name ||
          `${exportName.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${exportType}`;

        dispatch(
          completeExport({
            exportId,
            file_url: downloadUrl,
            file_name: finalFileName,
          })
        );

        toast.success("Export completed successfully!");

        if (onSuccess) {
          onSuccess(body, exportId);
        }

        return body;
      } else {
        // Mark export as failed
        dispatch(failExport({
          exportId,
          error: "Export failed - no file URL received"
        }));
        
        const errorMsg = "Export failed - no file URL received";
        toast.error(errorMsg);
        
        // Call error callback if provided
        if (onError) {
          onError(new Error(errorMsg), exportId);
        }
        
        throw new Error(errorMsg);
      }
    } catch (error) {
      // Mark export as failed
      const exportId = `export_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      dispatch(failExport({
        exportId,
        error: error.message || "Export failed"
      }));
      
      const errorMsg = `Export failed: ${error.message || "Unknown error"}`;
      toast.error(errorMsg);
      
      // Call error callback if provided
      if (onError) {
        onError(error, exportId);
      }
      
      throw error;
    }
  }, [dispatch]);

  /**
   * Quick export for common use cases
   * @param {Function} apiFunction - The API function to call
   * @param {Object} apiParams - Parameters for the API
   * @param {string} exportName - Name for the export
   * @returns {Promise} - Export promise
   */
  const quickExport = useCallback((apiFunction, apiParams, exportName) => {
    return exportData({
      apiFunction,
      apiParams,
      exportName: exportName || "Quick Export"
    });
  }, [exportData]);

  /**
   * Excel export helper
   * @param {Function} apiFunction - The API function to call
   * @param {Object} apiParams - Parameters for the API
   * @param {string} exportName - Name for the export
   * @returns {Promise} - Export promise
   */
  const exportToExcel = useCallback((apiFunction, apiParams, exportName) => {
    return exportData({
      apiFunction,
      apiParams,
      exportName: exportName || "Excel Export",
      exportType: "xlsx"
    });
  }, [exportData]);

  /**
   * PDF export helper
   * @param {Function} apiFunction - The API function to call
   * @param {Object} apiParams - Parameters for the API
   * @param {string} exportName - Name for the export
   * @returns {Promise} - Export promise
   */
  const exportToPDF = useCallback((apiFunction, apiParams, exportName) => {
    return exportData({
      apiFunction,
      apiParams,
      exportName: exportName || "PDF Export",
      exportType: "pdf"
    });
  }, [exportData]);

  /**
   * CSV export helper
   * @param {Function} apiFunction - The API function to call
   * @param {Object} apiParams - Parameters for the API
   * @param {string} exportName - Name for the export
   * @returns {Promise} - Export promise
   */
  const exportToCSV = useCallback((apiFunction, apiParams, exportName) => {
    return exportData({
      apiFunction,
      apiParams,
      exportName: exportName || "CSV Export",
      exportType: "csv"
    });
  }, [exportData]);

  return {
    exportData,
    quickExport,
    exportToExcel,
    exportToPDF,
    exportToCSV
  };
};

export default useExport;
